import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, CheckCircle2, Loader2, Plus, RotateCcw, XCircle } from "lucide-react";
import { formatApiError } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full p-2.5 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest";

export function TaskDuePill({ task }) {
  if (task.status !== "Open") return null;
  if (task.isOverdue)
    return <span className="rounded-full bg-red-100 text-red-800 px-2.5 py-0.5 text-[11px] font-bold">Overdue · {task.dueDate}</span>;
  if (task.dueToday)
    return <span className="rounded-full bg-yellow-100 text-yellow-800 px-2.5 py-0.5 text-[11px] font-bold">Due today</span>;
  return <span className="rounded-full bg-soft text-forest px-2.5 py-0.5 text-[11px] font-bold">Due {task.dueDate}</span>;
}

export default function TasksSection({ relatedType, relatedId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ title: "", dueDate: "", ownerEmail: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setError("");
      const r = await axios.get(`${API}/admin/tasks`, { params: { relatedType, relatedId } });
      setData(r.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [relatedType, relatedId]);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.dueDate) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(`${API}/admin/tasks`, {
        title: form.title.trim(),
        dueDate: form.dueDate,
        relatedType,
        relatedId,
        ...(form.ownerEmail ? { ownerEmail: form.ownerEmail } : {}),
      });
      setForm({ title: "", dueDate: "", ownerEmail: "" });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (task, status) => {
    try {
      await axios.patch(`${API}/admin/tasks/${task.id}`, { status });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const open = (data?.tasks || []).filter((t) => t.status === "Open");
  const closed = (data?.tasks || []).filter((t) => t.status !== "Open");

  return (
    <section data-testid={`tasks-section-${relatedType}`} className="rounded-2xl bg-white border border-border-neutral p-6">
      <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
        <CalendarClock className="h-4 w-4 text-mint" /> Follow-up tasks
      </h2>

      {error && (
        <p data-testid={`tasks-error-${relatedType}`} className="mt-3 text-sm text-red-700">{error}</p>
      )}

      <div className="mt-4 space-y-2">
        {data && open.length === 0 && (
          <p className="text-sm text-mid-neutral">No open tasks. Add a follow-up below.</p>
        )}
        {open.map((t) => (
          <div key={t.id} data-testid={`task-row-${t.id}`} className="flex flex-wrap items-center gap-3 rounded-xl bg-soft/50 p-3">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-forest truncate">{t.title}</p>
              <p className="text-xs text-mid-neutral">{t.ownerName || t.ownerEmail}</p>
            </div>
            <TaskDuePill task={t} />
            <button
              type="button"
              onClick={() => setStatus(t, "Done")}
              data-testid={`task-done-${t.id}`}
              className="inline-flex items-center gap-1 rounded-lg bg-forest text-white px-2.5 py-1.5 text-xs font-bold"
            >
              <CheckCircle2 className="h-3 w-3" /> Done
            </button>
            <button
              type="button"
              onClick={() => setStatus(t, "Cancelled")}
              data-testid={`task-cancel-${t.id}`}
              className="inline-flex items-center gap-1 rounded-lg bg-soft text-mid-neutral px-2.5 py-1.5 text-xs font-bold"
            >
              <XCircle className="h-3 w-3" /> Cancel
            </button>
          </div>
        ))}
        {closed.map((t) => (
          <div key={t.id} data-testid={`task-row-${t.id}`} className="flex items-center gap-3 rounded-xl p-3 opacity-60">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-mid-neutral line-through truncate">{t.title}</p>
              <p className="text-xs text-mid-neutral">{t.status} · due {t.dueDate}</p>
            </div>
            <button
              type="button"
              onClick={() => setStatus(t, "Open")}
              data-testid={`task-reopen-${t.id}`}
              className="inline-flex items-center gap-1 rounded-lg bg-soft text-forest px-2.5 py-1.5 text-xs font-bold"
            >
              <RotateCcw className="h-3 w-3" /> Reopen
            </button>
          </div>
        ))}
      </div>

      <form onSubmit={create} className="mt-4 rounded-xl border border-dashed border-border-neutral p-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <input
            required
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Call to follow up on proposal"
            data-testid={`task-title-${relatedType}`}
            className={inputCls + " bg-white"}
          />
          <input
            required
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
            data-testid={`task-due-${relatedType}`}
            className={inputCls + " bg-white"}
          />
          <select
            value={form.ownerEmail}
            onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
            data-testid={`task-owner-${relatedType}`}
            className={inputCls + " bg-white"}
          >
            <option value="">Assign to me</option>
            {(data?.assignees || []).map((a) => (
              <option key={a.id} value={a.email}>{a.name || a.email}</option>
            ))}
          </select>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="submit"
            disabled={!form.title.trim() || !form.dueDate || saving}
            data-testid={`task-submit-${relatedType}`}
            className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add task
          </button>
        </div>
      </form>
    </section>
  );
}
